import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Calendar as Cal, ChevronLeft, ChevronRight } from "lucide-react";

// All week-grid math happens in the BUSINESS timezone: the anchor is the Sunday
// (business-local calendar date) as an ISO date string, columns are calendar dates,
// and appointments bucket by their business-tz calendar date. This keeps headers,
// columns, and bookings consistent regardless of the viewer's browser timezone.
const bizDayStr = (d, tz) => d.toLocaleDateString("en-CA", { timeZone: tz });
const sundayOf = (dateStr) => {
  const d = new Date(dateStr + "T12:00:00Z"); // noon UTC avoids DST edges
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());
  return d.toISOString().slice(0, 10);
};
const shiftDays = (dateStr, n) => {
  const d = new Date(dateStr + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

export default function Appointments() {
  const [weekStart, setWeekStart] = useState(null); // business-tz Sunday, "YYYY-MM-DD"
  const [appts, setAppts] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [bizTz, setBizTz] = useState("America/Toronto");
  const [biz, setBiz] = useState(null);
  const [selected, setSelected] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [reschedStart, setReschedStart] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", service_id: "", start: "", notes: "" });

  const load = useCallback(async () => {
    const ws = weekStart || sundayOf(bizDayStr(new Date(), bizTz));
    if (!weekStart) setWeekStart(ws);
    const start = new Date(shiftDays(ws, -1) + "T00:00:00Z").toISOString();
    const end = new Date(shiftDays(ws, 8) + "T00:00:00Z").toISOString();
    const [a, s, c, b, st] = await Promise.all([
      api.get(`/appointments?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`),
      api.get("/services"), api.get("/customers"), api.get("/business"), api.get("/staff"),
    ]);
    setAppts(a.data); setServices(s.data); setCustomers(c.data); setBiz(b.data);
    setBizTz(b.data.timezone || "America/Toronto"); setStaff(st.data);
    // if the current week has nothing, jump to the week of the first upcoming appointment
    const currentSunday = sundayOf(bizDayStr(new Date(), b.data.timezone || bizTz));
    const inWeek = a.data.filter(x => bizDayStr(new Date(x.start), b.data.timezone || bizTz) >= ws
      && bizDayStr(new Date(x.start), b.data.timezone || bizTz) <= shiftDays(ws, 6));
    if (inWeek.length === 0 && ws === currentSunday) {
      const up = await api.get(`/appointments?start=${encodeURIComponent(new Date().toISOString())}&end=${encodeURIComponent(new Date(Date.now() + 90 * 86400000).toISOString())}`);
      if (up.data.length > 0) setWeekStart(sundayOf(bizDayStr(new Date(up.data[0].start), b.data.timezone || bizTz)));
    }
  }, [weekStart, bizTz]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const svc = services.find(s => s.id === form.service_id);
    if (!svc || !form.customer_id || !form.start) { toast.error("Fill all fields"); return; }
    const start = new Date(form.start);
    const end = new Date(start.getTime() + svc.duration_min * 60000);
    try {
      await api.post("/appointments", {
        customer_id: form.customer_id, service_id: form.service_id,
        start: start.toISOString(), end: end.toISOString(), notes: form.notes
      });
      toast.success("Appointment created"); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const toLocalInput = (iso) => {
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const cancelAppt = async () => {
    try {
      await api.delete(`/appointments/${selected.id}`);
      toast.success("Appointment cancelled"); setSelected(null); setConfirmCancel(false); load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };
  const saveResched = async () => {
    if (!reschedStart) { toast.error("Pick a new time"); return; }
    const svc = services.find(s => s.id === selected.service_id);
    const start = new Date(reschedStart);
    const end = new Date(start.getTime() + (svc?.duration_min || 30) * 60000);
    try {
      await api.patch(`/appointments/${selected.id}`, { start: start.toISOString(), end: end.toISOString() });
      toast.success("Appointment rescheduled"); setSelected(null); setRescheduling(false); load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const dayStrs = weekStart ? [...Array(7)].map((_, i) => shiftDays(weekStart, i)) : [];
  const custName = (id) => { const c = customers.find(x => x.id === id); return c ? `${c.first_name} ${c.last_name}`.trim() || c.phone : "—"; };
  const svcName = (id) => services.find(s => s.id === id)?.name || "—";
  const staffName = (id) => staff.find(s => s.id === id)?.name || "";
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: bizTz });
  const dayKey = (iso) => bizDayStr(new Date(iso), bizTz);
  const labelFor = (dateStr) => {
    const d = new Date(dateStr + "T12:00:00Z"); // noon UTC: same calendar date everywhere
    return {
      wd: d.toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" }),
      day: d.toLocaleDateString(undefined, { day: "numeric", timeZone: "UTC" }),
    };
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Appointments</h1>
          <p className="text-slate-600 mt-1">Your calendar, updated in real time.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="week-prev" variant="outline" size="icon" className="rounded-xl" onClick={() => setWeekStart(shiftDays(weekStart, -7))}><ChevronLeft className="w-4 h-4" /></Button>
          <div className="text-sm font-medium min-w-[180px] text-center">Week of {weekStart ? new Date(weekStart + "T12:00:00Z").toLocaleDateString(undefined, { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" }) : "…"}</div>
          <Button data-testid="week-next" variant="outline" size="icon" className="rounded-xl" onClick={() => setWeekStart(shiftDays(weekStart, 7))}><ChevronRight className="w-4 h-4" /></Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-appt-btn" className="rounded-xl bg-blue-600 hover:bg-blue-700 ml-2"><Plus className="w-4 h-4 mr-1" />New</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New appointment</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Customer</Label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger data-testid="appt-customer-select" className="mt-1.5"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.phone}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Service</Label>
                  <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                    <SelectTrigger data-testid="appt-service-select" className="mt-1.5"><SelectValue placeholder="Select service" /></SelectTrigger>
                    <SelectContent>{services.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · {s.price == null ? "Quote on-site" : `$${s.price}`} · {s.duration_min}m</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Start</Label><Input data-testid="appt-start-input" type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="mt-1.5" /></div>
                <Button data-testid="appt-save-btn" onClick={save} className="w-full rounded-xl bg-blue-600 hover:bg-blue-700">Create</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-x-auto" data-testid="calendar-grid">
        <div className="min-w-[700px]">
        <div className="grid grid-cols-7 border-b border-slate-200">
          {dayStrs.map((ds) => (
            <div key={ds} className="p-3 border-r border-slate-100 last:border-r-0 text-xs">
              <div className="font-semibold text-slate-500 uppercase tracking-wider">{labelFor(ds).wd}</div>
              <div className="mt-1 text-slate-950 font-heading font-bold text-lg">{labelFor(ds).day}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 min-h-[500px]">
          {dayStrs.map((ds) => {
            const dayAppts = appts.filter(a => dayKey(a.start) === ds);
            return (
              <div key={ds} className="p-2 border-r border-slate-100 last:border-r-0 space-y-1.5">
                {dayAppts.length === 0 && <div className="text-xs text-slate-300 text-center pt-6">—</div>}
                {dayAppts.map(a => (
                  <div key={a.id} onClick={() => { setSelected(a); setRescheduling(false); setConfirmCancel(false); }} className={`text-xs p-2 rounded-lg border cursor-pointer transition-colors ${a.status === "cancelled" ? "bg-slate-100 text-slate-400 line-through border-slate-200 hover:bg-slate-200" : "bg-blue-50 border-blue-200 text-blue-900 hover:bg-blue-100"}`} data-testid={`appt-block-${a.id}`}>
                    <div className="font-semibold">{fmtTime(a.start)} – {fmtTime(a.end)}</div>
                    <div className="truncate">{custName(a.customer_id)}{a.staff_id ? ` · ${staffName(a.staff_id)}` : ""}</div>
                    <div className="truncate text-blue-700">{svcName(a.service_id)}</div>
                    {a.notes && <div className="truncate text-blue-600/80 italic" title={a.notes}>{a.notes}</div>}
                    {a.created_by === "ai" && <div className="mt-1 text-[10px] uppercase tracking-wider text-blue-600 font-semibold">AI</div>}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setRescheduling(false); setConfirmCancel(false); } }}>
        <DialogContent data-testid="appt-detail-dialog">
          {selected && (() => {
            const c = customers.find(x => x.id === selected.customer_id);
            const svc = services.find(s => s.id === selected.service_id);
            const dateStr = new Date(selected.start).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: bizTz });
            return (
              <>
                <DialogHeader><DialogTitle>{svc?.name || "Appointment"}</DialogTitle></DialogHeader>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between gap-4"><span className="text-slate-500">When</span><span data-testid="appt-detail-when" className="font-medium text-right">{dateStr} · {fmtTime(selected.start)} – {fmtTime(selected.end)}</span></div>
                  <div className="flex justify-between gap-4"><span className="text-slate-500">Duration</span><span data-testid="appt-detail-duration" className="font-medium">{svc?.duration_min ?? "—"} min</span></div>
                  <div className="flex justify-between gap-4"><span className="text-slate-500">Cost of visit</span><span data-testid="appt-detail-price" className="font-medium">{svc ? (svc.price == null ? "Quote on-site" : `$${svc.price.toFixed(2)}`) : "—"}</span></div>
                  <div className="flex justify-between gap-4"><span className="text-slate-500">Customer</span><span data-testid="appt-detail-customer" className="font-medium text-right">{c ? `${c.first_name} ${c.last_name}`.trim() || c.phone : "—"}</span></div>
                  <div className="flex justify-between gap-4"><span className="text-slate-500">Phone</span><span data-testid="appt-detail-phone" className="font-medium">{c?.phone || "—"}</span></div>
                  {c?.email && <div className="flex justify-between gap-4"><span className="text-slate-500">Email</span><span data-testid="appt-detail-email" className="font-medium">{c.email}</span></div>}
                  {selected.staff_id && <div className="flex justify-between gap-4"><span className="text-slate-500">With</span><span data-testid="appt-detail-staff" className="font-medium">{staffName(selected.staff_id)}</span></div>}
                  <div className="flex justify-between gap-4"><span className="text-slate-500">Status</span><span data-testid="appt-detail-status" className="font-medium capitalize">{selected.status}</span></div>
                  <div className="flex justify-between gap-4"><span className="text-slate-500">Booked by</span><span data-testid="appt-detail-createdby" className="font-medium capitalize">{selected.created_by === "ai" ? "AI receptionist" : selected.created_by || "staff"}</span></div>
                  {selected.address && <div className="flex justify-between gap-4"><span className="text-slate-500">Service address</span><span data-testid="appt-detail-svcaddress" className="font-medium text-right">{selected.address}</span></div>}
                  {selected.customer_confirmed && <div className="flex justify-between gap-4"><span className="text-slate-500">Customer confirmed</span><span data-testid="appt-detail-confirmed" className="font-medium text-emerald-600">Yes — replied C by text</span></div>}
                  {biz?.address && <div className="flex justify-between gap-4"><span className="text-slate-500">Location</span><span data-testid="appt-detail-address" className="font-medium text-right">{biz.address}</span></div>}
                  {biz?.phone && <div className="flex justify-between gap-4"><span className="text-slate-500">Business phone</span><span data-testid="appt-detail-bizphone" className="font-medium">{biz.phone}</span></div>}
                  {selected.notes && <div className="pt-2 border-t border-slate-100"><span className="text-slate-500">Notes</span><p data-testid="appt-detail-notes" className="mt-1 text-slate-800">{selected.notes}</p></div>}
                  {selected.status !== "cancelled" && (
                    <div className="pt-3 border-t border-slate-100" data-testid="appt-actions">
                      {rescheduling ? (
                        <div className="space-y-2">
                          <Label>New start</Label>
                          <Input data-testid="appt-resched-input" type="datetime-local" value={reschedStart} onChange={(e) => setReschedStart(e.target.value)} />
                          <div className="flex gap-2">
                            <Button data-testid="appt-resched-save" onClick={saveResched} className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-700">Save new time</Button>
                            <Button data-testid="appt-resched-back" variant="outline" className="rounded-xl" onClick={() => setRescheduling(false)}>Back</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button data-testid="appt-resched-open" variant="outline" className="flex-1 rounded-xl" onClick={() => { setReschedStart(toLocalInput(selected.start)); setRescheduling(true); }}>Reschedule</Button>
                          {confirmCancel ? (
                            <Button data-testid="appt-cancel-confirm" onClick={cancelAppt} className="flex-1 rounded-xl bg-red-600 hover:bg-red-700">Confirm cancel?</Button>
                          ) : (
                            <Button data-testid="appt-cancel-open" variant="outline" className="flex-1 rounded-xl text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirmCancel(true)}>Cancel</Button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
